import React, { useEffect, useState } from 'react';
// Enhanced types for user behavior tracking
export interface UserBehavior {
  viewedProducts: string[];
  clickedProducts: string[];
  purchasedProducts: string[];
  viewedCategories: Record<string, number>; // category -> view count
  searchTerms: string[];
  chatTopics: string[];
  viewDuration: Record<string, number>; // product id -> seconds viewed
  lastActivity: Record<string, Date>; // activity type -> timestamp
  engagementScores: Record<string, number>; // product id -> engagement score
  sessionCount: number;
  totalTimeSpent: number;
  conversionRate: number;
  recommendationClicks: number;
  totalRecommendationsViewed: number;
  productComparisonCount: number;
}
// Enhanced types for user preferences
export interface UserPreferences {
  favoriteCategories: string[];
  preferredPriceRange: {
    min: number;
    max: number;
  };
  sensitivities: string[]; // Things user has shown negative reaction to
  explicitPreferences: string[]; // Directly stated preferences
  inferred: Record<string, number>; // feature -> weight (0-1)
  personalityTraits: Record<string, number>; // trait -> weight (0-1)
  seasonalPreferences: string[];
  timeSensitivePreferences: {
    morningPreferences: string[];
    eveningPreferences: string[];
    weekendPreferences: string[];
  };
  socialInfluence: Record<string, number>; // influencer -> weight (0-1)
  recommendationFeedback: {
    positiveIds: string[];
    negativeIds: string[];
  };
}
// Product interface
export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discount: number;
  rating: number;
  reviews: number;
  image: string;
  category: string;
  tags: string[];
  features?: string[];
  bestseller?: boolean;
  connected?: boolean;
}
// Initialize default user behavior with enhanced fields
const defaultUserBehavior: UserBehavior = {
  viewedProducts: [],
  clickedProducts: [],
  purchasedProducts: [],
  viewedCategories: {},
  searchTerms: [],
  chatTopics: [],
  viewDuration: {},
  lastActivity: {
    productView: new Date(),
    search: new Date(),
    chat: new Date(),
    purchase: new Date()
  },
  engagementScores: {},
  sessionCount: 1,
  totalTimeSpent: 0,
  conversionRate: 0,
  recommendationClicks: 0,
  totalRecommendationsViewed: 0,
  productComparisonCount: 0
};
// Initialize default user preferences with enhanced fields
const defaultUserPreferences: UserPreferences = {
  favoriteCategories: [],
  preferredPriceRange: {
    min: 0,
    max: 1000
  },
  sensitivities: [],
  explicitPreferences: [],
  inferred: {
    price: 0.5,
    quality: 0.5,
    novelty: 0.5,
    discount: 0.5 // neutral discount preference
  },
  personalityTraits: {
    adventurous: 0.5,
    practical: 0.5,
    spontaneous: 0.5,
    analytical: 0.5,
    social: 0.5
  },
  seasonalPreferences: [],
  timeSensitivePreferences: {
    morningPreferences: [],
    eveningPreferences: [],
    weekendPreferences: []
  },
  socialInfluence: {},
  recommendationFeedback: {
    positiveIds: [],
    negativeIds: []
  }
};
// Load user behavior from localStorage
export const getUserBehavior = (): UserBehavior => {
  try {
    const saved = localStorage.getItem('userBehavior');
    return saved ? JSON.parse(saved) : {
      ...defaultUserBehavior
    };
  } catch (error) {
    console.error('Failed to load user behavior:', error);
    return {
      ...defaultUserBehavior
    };
  }
};
// Load user preferences from localStorage
export const getUserPreferences = (): UserPreferences => {
  try {
    const saved = localStorage.getItem('userPreferences');
    return saved ? JSON.parse(saved) : {
      ...defaultUserPreferences
    };
  } catch (error) {
    console.error('Failed to load user preferences:', error);
    return {
      ...defaultUserPreferences
    };
  }
};
// Save user behavior to localStorage
export const saveUserBehavior = (behavior: UserBehavior): void => {
  try {
    localStorage.setItem('userBehavior', JSON.stringify(behavior));
  } catch (error) {
    console.error('Failed to save user behavior:', error);
  }
};
// Save user preferences to localStorage
export const saveUserPreferences = (preferences: UserPreferences): void => {
  try {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  } catch (error) {
    console.error('Failed to save user preferences:', error);
  }
};
// Track product view
export const trackProductView = (productId: string, category: string): void => {
  const behavior = getUserBehavior();
  // Add to viewed products if not already there
  if (!behavior.viewedProducts.includes(productId)) {
    behavior.viewedProducts.unshift(productId);
    // Keep only the last 20 viewed products
    behavior.viewedProducts = behavior.viewedProducts.slice(0, 20);
  }
  // Increment category view count
  behavior.viewedCategories[category] = (behavior.viewedCategories[category] || 0) + 1;
  // Update last activity
  behavior.lastActivity.productView = new Date();
  saveUserBehavior(behavior);
  // Update preferences based on views
  updatePreferencesFromBehavior(behavior);
};
// Track product click
export const trackProductClick = (productId: string): void => {
  const behavior = getUserBehavior();
  // Add to clicked products if not already there
  if (!behavior.clickedProducts.includes(productId)) {
    behavior.clickedProducts.unshift(productId);
    // Keep only the last 20 clicked products
    behavior.clickedProducts = behavior.clickedProducts.slice(0, 20);
  }
  saveUserBehavior(behavior);
  // Update preferences based on clicks
  updatePreferencesFromBehavior(behavior);
};
// Track product purchase
export const trackProductPurchase = (productId: string): void => {
  const behavior = getUserBehavior();
  // Add to purchased products if not already there
  if (!behavior.purchasedProducts.includes(productId)) {
    behavior.purchasedProducts.unshift(productId);
  }
  // Update last activity
  behavior.lastActivity.purchase = new Date();
  saveUserBehavior(behavior);
  // Update preferences based on purchases
  updatePreferencesFromBehavior(behavior);
};
// Track search term
export const trackSearchTerm = (term: string): void => {
  const behavior = getUserBehavior();
  // Add to search terms
  behavior.searchTerms.unshift(term);
  // Keep only the last 10 search terms
  behavior.searchTerms = behavior.searchTerms.slice(0, 10);
  // Update last activity
  behavior.lastActivity.search = new Date();
  saveUserBehavior(behavior);
};
// Track chat topic
export const trackChatTopic = (topic: string): void => {
  const behavior = getUserBehavior();
  // Add to chat topics
  behavior.chatTopics.unshift(topic);
  // Keep only the last 10 chat topics
  behavior.chatTopics = behavior.chatTopics.slice(0, 10);
  // Update last activity
  behavior.lastActivity.chat = new Date();
  saveUserBehavior(behavior);
};
// Track recommendation view
export const trackRecommendationView = (recommendationIds: string[]): void => {
  const behavior = getUserBehavior();
  // Increment total recommendations viewed
  behavior.totalRecommendationsViewed += recommendationIds.length;
  // Update last activity
  behavior.lastActivity.productView = new Date();
  saveUserBehavior(behavior);
};
// Track recommendation click
export const trackRecommendationClick = (productId: string): void => {
  const behavior = getUserBehavior();
  // Increment recommendation clicks
  behavior.recommendationClicks += 1;
  // Calculate conversion rate
  if (behavior.totalRecommendationsViewed > 0) {
    behavior.conversionRate = behavior.recommendationClicks / behavior.totalRecommendationsViewed;
  }
  saveUserBehavior(behavior);
  // Also track as a regular product click
  trackProductClick(productId);
};
// Track product comparison
export const trackProductComparison = (productIds: string[]): void => {
  const behavior = getUserBehavior();
  // Increment comparison count
  behavior.productComparisonCount += 1;
  // Mark products as compared
  productIds.forEach(id => {
    behavior.engagementScores[id] = (behavior.engagementScores[id] || 0) + 0.5;
  });
  saveUserBehavior(behavior);
};
// Track time spent viewing a product
export const trackViewDuration = (productId: string, seconds: number): void => {
  const behavior = getUserBehavior();
  // Update view duration
  behavior.viewDuration[productId] = (behavior.viewDuration[productId] || 0) + seconds;
  // Update total time spent
  behavior.totalTimeSpent += seconds;
  // Update engagement score based on view duration
  behavior.engagementScores[productId] = (behavior.engagementScores[productId] || 0) + seconds / 60 * 0.1;
  saveUserBehavior(behavior);
};
// Track positive feedback on recommendation
export const trackPositiveFeedback = (productId: string): void => {
  const preferences = getUserPreferences();
  // Add to positive feedback
  if (!preferences.recommendationFeedback.positiveIds.includes(productId)) {
    preferences.recommendationFeedback.positiveIds.push(productId);
  }
  // Remove from negative feedback if present
  preferences.recommendationFeedback.negativeIds = preferences.recommendationFeedback.negativeIds.filter(id => id !== productId);
  saveUserPreferences(preferences);
};
// Track negative feedback on recommendation
export const trackNegativeFeedback = (productId: string): void => {
  const preferences = getUserPreferences();
  // Add to negative feedback
  if (!preferences.recommendationFeedback.negativeIds.includes(productId)) {
    preferences.recommendationFeedback.negativeIds.push(productId);
  }
  // Remove from positive feedback if present
  preferences.recommendationFeedback.positiveIds = preferences.recommendationFeedback.positiveIds.filter(id => id !== productId);
  saveUserPreferences(preferences);
};
// Track session start
export const trackSessionStart = (): void => {
  const behavior = getUserBehavior();
  // Increment session count
  behavior.sessionCount += 1;
  saveUserBehavior(behavior);
};
// Update preferences based on behavior
const updatePreferencesFromBehavior = (behavior: UserBehavior): void => {
  const preferences = getUserPreferences();
  // Update favorite categories
  const categoryEntries = Object.entries(behavior.viewedCategories);
  if (categoryEntries.length > 0) {
    // Sort categories by view count
    categoryEntries.sort((a, b) => b[1] - a[1]);
    // Take top 3 categories
    preferences.favoriteCategories = categoryEntries.slice(0, 3).map(entry => entry[0]);
  }
  // Infer price sensitivity if user has viewed products
  if (behavior.viewedProducts.length > 0) {
    // This would normally pull price data from a product database
    // For now we'll just set a placeholder value
    preferences.inferred.price = 0.7; // Slightly price sensitive
  }
  // Infer discount preference if user has clicked on discounted products
  if (behavior.clickedProducts.length > 0) {
    // This would normally check if clicked products had discounts
    // For now we'll just set a placeholder value
    preferences.inferred.discount = 0.8; // Likes discounts
  }
  // Infer quality preference if user has purchased high-rated products
  if (behavior.purchasedProducts.length > 0) {
    // This would normally check ratings of purchased products
    // For now we'll just set a placeholder value
    preferences.inferred.quality = 0.9; // Values quality
  }
  saveUserPreferences(preferences);
};
// Get personalized recommendations
export const getPersonalizedRecommendations = <T extends Product,>(products: T[], count: number = 4): {
  items: T[];
  reasons: Record<string, string>;
} => {
  const behavior = getUserBehavior();
  const preferences = getUserPreferences();
  // Calculate scores for each product
  const scoredProducts = products.map(product => {
    let score = 0;
    const reasons: string[] = [];
    // Higher score for favorite categories
    if (preferences.favoriteCategories.includes(product.category)) {
      score += 2;
      reasons.push(`Based on your interest in ${product.category} products`);
    }
    // Higher score for previously viewed products
    if (behavior.viewedProducts.includes(product.id)) {
      score += 1;
      reasons.push('Based on your browsing history');
    }
    // Higher score for previously clicked products
    if (behavior.clickedProducts.includes(product.id)) {
      score += 1.5;
      reasons.push('You showed interest in this before');
    }
    // Higher score for products with discounts if user likes discounts
    if (product.discount > 0 && preferences.inferred.discount > 0.6) {
      score += product.discount / 100 * 3 * preferences.inferred.discount;
      reasons.push('Special discount available');
    }
    // Higher score for bestsellers if user values quality
    if (product.bestseller && preferences.inferred.quality > 0.6) {
      score += 1.5 * preferences.inferred.quality;
      reasons.push('Popular with customers like you');
    }
    // Higher score for connected devices if user has shown interest
    if (product.connected && behavior.viewedProducts.some(id => products.find(p => p.id === id)?.connected)) {
      score += 2;
      reasons.push('Compatible with your interests');
    }
    // Add some randomness to avoid always showing the same products
    score += Math.random() * 0.5;
    return {
      product,
      score,
      reason: reasons.length > 0 ? reasons[0] : 'Recommended for you'
    };
  });
  // Sort by score and take the top N
  scoredProducts.sort((a, b) => b.score - a.score);
  const topProducts = scoredProducts.slice(0, count);
  // Create reasons map
  const reasons: Record<string, string> = {};
  topProducts.forEach(({
    product,
    reason
  }) => {
    reasons[product.id] = reason;
  });
  return {
    items: topProducts.map(item => item.product),
    reasons
  };
};
// React hook for personalized recommendations
export const usePersonalizedRecommendations = <T extends Product,>(products: T[], count: number = 4): {
  items: T[];
  reasons: Record<string, string>;
  loading: boolean;
} => {
  const [recommendations, setRecommendations] = useState<{
    items: T[];
    reasons: Record<string, string>;
  }>({
    items: [],
    reasons: {}
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Simulate async loading for realism
    setLoading(true);
    const timer = setTimeout(() => {
      const result = getPersonalizedRecommendations(products, count);
      setRecommendations(result);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [products, count]);
  return {
    ...recommendations,
    loading
  };
};
// For backward compatibility, make the enhanced version the default
export const getEnhancedPersonalizedRecommendations = getPersonalizedRecommendations;
export const useEnhancedPersonalizedRecommendations = usePersonalizedRecommendations;